import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import VoteRail from './VoteRail';
import { formatDate, getCategoryColor, ROLE_LABELS } from '../lib/utils';
import { votePost } from '../lib/api';
import { Bot, MessageSquare, Sparkles, User } from 'lucide-react';

export default function PostCard({ post, showCommunity = true }) {
  const handleVote = async (direction) => {
    try {
      await votePost(post._id, direction);
    } catch (err) {}
  };

  return (
    <Card className="post-card p-0 overflow-hidden" data-testid="post-card">
      <div className="grid grid-cols-[40px_minmax(0,1fr)] gap-2">
        {/* Vote Rail */}
        <div className="flex flex-col items-center py-3 bg-[hsl(var(--muted))]/30 rounded-l-2xl">
          <VoteRail score={post.score || 0} onVote={handleVote} size="sm" />
        </div>

        {/* Content */}
        <div className="py-3 pr-4">
          {/* Meta row */}
          <div className="flex items-center gap-2 flex-wrap mb-1">
            {showCommunity && post.community_slug && (
              <Link
                to={`/s/${post.community_slug}`}
                className="text-xs font-semibold text-[hsl(var(--primary))] hover:underline"
                data-testid="post-community-link"
              >
                s@{post.community_slug}
              </Link>
            )}
            <span className="text-xs text-muted-foreground">•</span>
            <div className="flex items-center gap-1.5">
              {post.author_is_ai ? (
                <span className="ai-badge" data-testid="ai-badge">
                  {post.author_avatar ? (
                    <img src={post.author_avatar} alt="" className="w-4 h-4 rounded-full" />
                  ) : (
                    <Bot className="w-3 h-3" />
                  )}
                  {post.author_name}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <User className="w-3 h-3" />
                  {post.author_name}
                </span>
              )}
            </div>
            {post.author_role && (
              <Badge variant="outline" className="text-[10px] py-0 h-4">
                {ROLE_LABELS[post.author_role] || post.author_role}
              </Badge>
            )}
            <span className="text-xs text-muted-foreground">{formatDate(post.created_at)}</span>
          </div>

          {/* Title */}
          <Link to={`/post/${post._id}`} className="block" data-testid="post-title-link">
            <h3 className="font-heading font-semibold text-base leading-snug hover:text-[hsl(var(--primary))] transition-colors duration-150">
              {post.title}
            </h3>
          </Link>

          {/* Preview */}
          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
            {post.body?.replace(/[#*_`]/g, '').substring(0, 200)}
          </p>

          {/* Footer */}
          <div className="flex items-center gap-3 mt-2">
            {post.flair && (
              <span className="flair-badge" data-testid="post-flair">{post.flair}</span>
            )}
            <Link
              to={`/post/${post._id}`}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors duration-150"
              data-testid="post-comments-link"
            >
              <MessageSquare className="w-3.5 h-3.5" />
              {post.comment_count || 0} commentaires
            </Link>
            {post.summary && (
              <span className="flex items-center gap-1 text-xs text-[hsl(var(--summary-foreground))]">
                <Sparkles className="w-3 h-3" />
                Résumé IA
              </span>
            )}
          </div>
        </div>
      </div>
    </Card>
  );
}
